import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import ContextualBreadcrumb from '../../components/ui/ContextualBreadcrumb';
import ProjectBasicInfo from './components/ProjectBasicInfo';
import PhaseTimeline from './components/PhaseTimeline';
import FormActions from './components/FormActions';
import { projectService } from '../../services/projectService';
import { supabase } from '../../lib/supabase';
import { Upload, FileText, X, Loader, Sparkles, CheckCircle2 } from 'lucide-react';

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 40; // ~2 minutes

const ProjectCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfUploadedPath, setPdfUploadedPath] = useState(null);
  const [pdfError, setPdfError] = useState('');

  // AI polling state
  const [aiPolling, setAiPolling] = useState(false);
  const [aiPollingMessage, setAiPollingMessage] = useState('');
  const pollTimerRef = useRef(null);
  const pollAttemptsRef = useRef(0);
  const uploadedAtRef = useRef(null);

  const [formData, setFormData] = useState({
    projectName: '',
    estimatedStartDate: '',
    estimatedEndDate: ''
  });

  const [phases, setPhases] = useState({});
  const [errors, setErrors] = useState({});

  // Standard phases list (same order as PhaseTimeline)
  const standardPhases = [
    { id: 1, name: 'Hacking' },
    { id: 2, name: '3 Musketeers' },
    { id: 3, name: 'False Ceiling' },
    { id: 4, name: 'Tiling' },
    { id: 5, name: 'Carpentry Measurement' },
    { id: 6, name: 'Clearing' },
    { id: 7, name: 'Painting' },
    { id: 8, name: 'Carpentry Installation' },
    { id: 9, name: '3 Musketeers Installation' },
    { id: 10, name: 'Paint Touch-Up' },
    { id: 11, name: 'Cleaning' },
    { id: 12, name: 'Handover' }
  ];

  // Load all users (workers) from the users table on mount
  useEffect(() => {
    const loadWorkers = async () => {
      try {
        const { data, error } = await supabase
          ?.from('users')
          ?.select('id, full_name, email')
          ?.order('full_name', { ascending: true });
        if (!error && data) {
          setWorkers(data);
        }
      } catch (err) {
        console.error('Failed to load workers:', err);
      }
    };
    loadWorkers();
  }, []);

  // Pre-populate form when editing an AI draft
  useEffect(() => {
    const draftData = location?.state?.draftData;
    if (!draftData) return;

    setFormData({
      projectName: draftData?.name || '',
      estimatedStartDate: draftData?.start_date || '',
      estimatedEndDate: draftData?.end_date || ''
    });

    const draftPhases = draftData?.phases || [];
    if (draftPhases?.length > 0) {
      const mappedPhases = {};
      draftPhases?.forEach((draftPhase) => {
        const tradeName = (draftPhase?.phase_name || draftPhase?.required_trade || '')?.toLowerCase();
        const matched = standardPhases?.find(
          sp => sp?.name?.toLowerCase() === tradeName || tradeName?.includes(sp?.name?.toLowerCase())
        );
        if (matched) {
          mappedPhases[matched.id] = {
            enabled: true,
            startDate: draftPhase?.planned_start_date || '',
            endDate: draftPhase?.planned_end_date || '',
            assigned_worker_id: draftPhase?.assigned_worker_id || draftPhase?.worker?.id || ''
          };
        }
      });
      if (Object.keys(mappedPhases)?.length > 0) {
        setPhases(mappedPhases);
      }
    }
  }, [location?.state]);

  // Cleanup poll timer on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef?.current) clearInterval(pollTimerRef?.current);
    };
  }, []);

  const startAiProjectPolling = (uploadTimestamp) => {
    setAiPolling(true);
    setAiPollingMessage('Analyzing your quotation PDF…');
    pollAttemptsRef.current = 0;

    const messages = [
      'Analyzing your quotation PDF…',
      'Parsing cost items and phases…',
      'Grouping tasks by trade…',
      'Building your project structure…',
      'Almost ready…',
    ];

    pollTimerRef.current = setInterval(async () => {
      pollAttemptsRef.current += 1;

      // Rotate status messages
      const msgIndex = Math.min(
        Math.floor(pollAttemptsRef?.current / 3),
        messages?.length - 1
      );
      setAiPollingMessage(messages?.[msgIndex]);

      if (pollAttemptsRef?.current >= POLL_MAX_ATTEMPTS) {
        clearInterval(pollTimerRef?.current);
        setAiPolling(false);
        setPdfError('Project generation is taking longer than expected. Please check your projects list shortly.');
        return;
      }

      try {
        // Look for a newly created ai_import project created after upload
        const { data: newProjects, error } = await supabase
          ?.from('projects')
          ?.select('id, name, source, created_at')
          ?.eq('source', 'ai_import')
          ?.gte('created_at', uploadTimestamp)
          ?.order('created_at', { ascending: false })
          ?.limit(1);

        if (!error && newProjects?.length > 0) {
          clearInterval(pollTimerRef?.current);
          setAiPolling(false);
          setAiPollingMessage('');
          const newProject = newProjects?.[0];
          navigate('/phase-management', {
            state: {
              projectId: newProject?.id,
              projectName: newProject?.name,
            }
          });
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, POLL_INTERVAL_MS);
  };

  const handleBasicInfoChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhaseChange = (phaseId, field, value) => {
    setPhases(prev => ({
      ...prev,
      [phaseId]: { ...prev?.[phaseId], [field]: value }
    }));
    if (errors?.[`phase_${phaseId}_${field}`] || errors?.[`phase_${phaseId}_worker`]) {
      setErrors(prev => ({
        ...prev,
        [`phase_${phaseId}_${field}`]: '',
        [`phase_${phaseId}_worker`]: ''
      }));
    }
  };

  const handlePdfSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (file?.type !== 'application/pdf') {
      setPdfError('Only PDF files are allowed.');
      return;
    }
    if (file?.size > 20 * 1024 * 1024) {
      setPdfError('File size must be under 20MB.');
      return;
    }
    setPdfError('');
    setPdfFile(file);
    setPdfUploadedPath(null);
  };

  const handlePdfUpload = async () => {
    if (!pdfFile) return;
    setPdfUploading(true);
    setPdfError('');

    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) {
        setPdfError('You must be logged in to upload files.');
        return;
      }

      const timestamp = Date.now();
      const safeName = pdfFile?.name?.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `${session?.user?.id}/${timestamp}-${safeName}`;

      const { error } = await supabase?.storage
        ?.from('project-pdfs')
        ?.upload(filePath, pdfFile, { cacheControl: '3600', upsert: false });

      if (error) throw error;

      setPdfUploadedPath(filePath);

      // Immediately create a draft project in Supabase so it's saved under Drafts
      // even if the user leaves before AI processing completes
      const draftName = pdfFile?.name?.replace(/\.pdf$/i, '')?.replace(/[_-]/g, ' ') || 'Untitled Project';
      const { error: draftError } = await supabase
        ?.from('projects')
        ?.insert({
          name: draftName,
          status: 'draft',
          source: 'ai_import',
          created_by: session?.user?.id,
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        });

      if (draftError) {
        console.warn('Could not pre-save draft project:', draftError?.message);
        // Non-fatal — still proceed with polling
      }

      // Record the upload time and start polling for the AI-created project
      const uploadedAt = new Date()?.toISOString();
      uploadedAtRef.current = uploadedAt;
      startAiProjectPolling(uploadedAt);
    } catch (err) {
      console.error('PDF upload error:', err);
      setPdfError(err?.message || 'Failed to upload PDF. Please try again.');
    } finally {
      setPdfUploading(false);
    }
  };

  const handleRemovePdf = () => {
    if (pollTimerRef?.current) clearInterval(pollTimerRef?.current);
    setAiPolling(false);
    setAiPollingMessage('');
    setPdfFile(null);
    setPdfUploadedPath(null);
    setPdfError('');
    pollAttemptsRef.current = 0;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.projectName?.trim()) newErrors.projectName = 'Project name is required';
    if (!formData?.estimatedStartDate) newErrors.estimatedStartDate = 'Start date is required';
    if (!formData?.estimatedEndDate) newErrors.estimatedEndDate = 'End date is required';
    if (formData?.estimatedStartDate && formData?.estimatedEndDate) {
      if (new Date(formData.estimatedEndDate) <= new Date(formData.estimatedStartDate)) {
        newErrors.estimatedEndDate = 'End date must be after start date';
      }
    }
    Object.entries(phases)?.forEach(([phaseId, phaseData]) => {
      if (phaseData?.enabled) {
        if (!phaseData?.assigned_worker_id) newErrors[`phase_${phaseId}_worker`] = 'Please assign a worker to this phase';
        if (!phaseData?.startDate) newErrors[`phase_${phaseId}_start`] = 'Start date required when enabled';
        if (!phaseData?.endDate) newErrors[`phase_${phaseId}_end`] = 'End date required when enabled';
        if (phaseData?.startDate && phaseData?.endDate) {
          if (new Date(phaseData.endDate) <= new Date(phaseData.startDate)) {
            newErrors[`phase_${phaseId}_end`] = 'End date must be after start date';
          }
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSaveDraft = () => {
    const draftData = {
      ...formData,
      phases,
      status: 'draft',
      savedAt: new Date()?.toISOString()
    };
    localStorage.setItem('projectDraft', JSON.stringify(draftData));
    alert('Draft saved successfully!');
  };

  const handleCreateProject = async () => {
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setIsSubmitting(true);
    setErrors({});
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) {
        setErrors({ general: 'You must be logged in to create a project' });
        setIsSubmitting(false);
        return;
      }
      const projectData = {
        name: formData?.projectName,
        startDate: formData?.estimatedStartDate,
        endDate: formData?.estimatedEndDate,
        status: 'on-track',
        currentPhase: null,
        phases: phases
      };
      const createdProject = await projectService?.createProject(projectData);
      localStorage.removeItem('projectDraft');
      navigate('/boss-home-page', {
        state: { message: 'Project created successfully!', projectId: createdProject?.id }
      });
    } catch (error) {
      console.error('Error creating project:', error);
      setErrors({ general: error?.message || 'Failed to create project. Please try again.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (formData?.projectName) {
      const confirmCancel = window.confirm('You have unsaved changes. Do you want to discard them?');
      if (!confirmCancel) return;
    }
    navigate('/boss-home-page');
  };

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation />
      <ContextualBreadcrumb />
      <main className="main-content max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Create New Project</h1>
          <p className="text-base text-muted-foreground">
            Set up your renovation project with timeline and worker assignments
          </p>
        </div>

        {errors?.general && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive">{errors?.general}</p>
          </div>
        )}

        <div className="space-y-6">
          {/* PDF Upload Section */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-foreground">AI Scheduling</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Upload a quotation PDF and we'll automatically create your project with phases and cost breakdown.
            </p>

            {/* AI Polling State */}
            {aiPolling && (
              <div className="flex flex-col items-center justify-center gap-3 py-8 px-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <Loader className="w-6 h-6 text-primary animate-spin" />
                  <span className="text-sm font-medium text-primary">{aiPollingMessage}</span>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Your project is being generated in the background. You'll be redirected automatically once it's ready.
                </p>
                <div className="flex gap-1 mt-1">
                  {[0, 1, 2]?.map(i => (
                    <span
                      key={i}
                      className="w-2 h-2 rounded-full bg-primary animate-bounce"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Upload UI (hidden while polling) */}
            {!aiPolling && (
              <>
                {!pdfFile ? (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors">
                    <Upload className="w-7 h-7 text-muted-foreground mb-2" />
                    <span className="text-sm text-muted-foreground">Click to select a PDF file</span>
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={handlePdfSelect}
                    />
                  </label>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-muted/40 border border-border rounded-lg">
                    <FileText className="w-8 h-8 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{pdfFile?.name}</p>
                      <p className="text-xs text-muted-foreground">{(pdfFile?.size / 1024 / 1024)?.toFixed(2)} MB</p>
                      {pdfUploadedPath && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                          <p className="text-xs text-green-600 font-medium">Uploaded — generating project…</p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!pdfUploadedPath && (
                        <button
                          type="button"
                          onClick={handlePdfUpload}
                          disabled={pdfUploading}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-md hover:bg-primary/90 disabled:opacity-60 transition-colors"
                        >
                          {pdfUploading ? (
                            <><Loader className="w-3.5 h-3.5 animate-spin" /> Uploading...</>
                          ) : (
                            <><Upload className="w-3.5 h-3.5" /> Upload</>
                          )}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleRemovePdf}
                        disabled={pdfUploading}
                        className="p-1.5 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-60"
                        title="Remove file"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {pdfError && (
              <p className="mt-2 text-xs text-destructive">{pdfError}</p>
            )}
          </div>

          <ProjectBasicInfo
            formData={formData}
            errors={errors}
            onChange={handleBasicInfoChange}
          />

          <PhaseTimeline
            phases={phases}
            errors={errors}
            onChange={handlePhaseChange}
            workers={workers}
          />

          <FormActions
            onSaveDraft={handleSaveDraft}
            onCreateProject={handleCreateProject}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
          />
        </div>
      </main>
    </div>
  );
};

export default ProjectCreation;